import { ActionTypes } from '../actions/leaderboardV2_index';
import { ActionTypes as EventActionTypes } from '../actions/event_index';
import { ActionTypes as ScoringStatusActionTypes } from '../actions/scoring_status_index';

// Single source of truth for the slice shape and its defaults. The store is
// always created with a preloaded state (the Rails initial-state hash), which
// bypasses the reducer's `state = initialState` default — so the root spreads
// this factory UNDER the Rails props to guarantee every key is well-defined
// even when the backend omits it.
export function createLeaderboardV2State() {
  return {
    loading: false,
    error: null,
    // True when the last tournaments-list response was empty (the round has
    // no displayable tournaments). Computed in the FETCH_EVENTS_SUCCESS case,
    // atomically with eventsList, so every dispatch site (initial fetch and
    // poller) keeps it consistent; false before the first response, so the
    // empty-state message never flashes pre-fetch.
    emptyEventsList: false,
    leagueId: '',
    leagueName: '',
    roundId: '',
    roundList: [],
    roundListEtag: null,
    colorScores: {},
    eventsList: {},
    eventsListEtag: null,
    eventsListRoundId: null,
    hidePurse: false,
    context: '',
    roundsUrl: '',
    tournamentsUrl: '',
    tournamentUrl: '',
    aggregatesUrl: '',
    memberProfilesUrl: '',
    summariesUrls: {},
    displaySummaries: {},
    memberSearchUrl: '',
    memberAggregatesUrl: '',
    holeByHoleView: 'colors',
    stateFlagUrls: {},
    favoritesByEvent: {},
    pollMinInterval: 10000,
    pollMultiplier: null,
    pollMaxInterval: null,
    incrementalWaitTimeDisabled: false,
    scoringStatus: null,
    orientationLocked: false,
  };
}

const initialState = createLeaderboardV2State();

export default function leaderboardV2Reducer(state = initialState, action) {
  switch (action.type) {
    case ActionTypes.FETCH_EVENTS_REQUEST:
      return { ...state, loading: true, error: null };

    case ActionTypes.FETCH_EVENTS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        emptyEventsList: Object.keys(action.payload.eventsList).length === 0,
        leagueId: action.payload.leagueId,
        leagueName: action.payload.leagueName,
        eventsList: action.payload.eventsList,
        eventsListEtag: action.payload.etag ?? null,
        eventsListRoundId: state.roundId,
        hidePurse: action.payload.hidePurse ?? state.hidePurse,
      };

    case ActionTypes.FETCH_EVENTS_FAILURE:
      return { ...state, loading: false, error: action.error };

    case ActionTypes.SET_ROUND_ID:
      return { ...state, roundId: action.payload, eventsListEtag: null };

    case ActionTypes.SET_ROUND_ONGOING: {
      const { roundId, ongoing } = action.payload;
      return {
        ...state,
        roundList: (state.roundList ?? []).map((round) =>
          String(round.id) === String(roundId) ? { ...round, ongoing } : round
        ),
      };
    }

    case ActionTypes.SET_ROUND_LIST: {
      const previousById = new Map((state.roundList ?? []).map((round) => [String(round.id), round]));
      return {
        ...state,
        // The rounds endpoint never computes the per-round ongoing flag (that
        // would cost one cache fetch per round per modal open) - carry over
        // whatever the client already learned from tournaments responses.
        roundList: action.payload.rounds.map((round) => {
          const previous = previousById.get(String(round.id));
          return previous?.ongoing === undefined ? round : { ...round, ongoing: previous.ongoing };
        }),
        roundListEtag: action.payload.etag ?? null,
      };
    }

    case ActionTypes.SET_ORIENTATION_LOCKED:
      return { ...state, orientationLocked: action.payload };

    case ActionTypes.COMMIT_EVENTS:
      return {
        ...state,
        roundId: action.payload.roundId,
        // Kept atomic with eventsList, like FETCH_EVENTS_SUCCESS: committing a
        // round's preview is as truthful as fetching it - a stale flag would
        // blank the screen after committing an empty round, or show the
        // empty-state message on top of a committed non-empty one.
        emptyEventsList: Object.keys(action.payload.eventsList).length === 0,
        eventsList: action.payload.eventsList,
        eventsListRoundId: action.payload.roundId,
        eventsListEtag: null,
      };

    case ScoringStatusActionTypes.FETCH_SCORING_STATUS_SUCCESS:
      return { ...state, scoringStatus: action.payload.messages };

    case EventActionTypes.TOGGLE_FAVORITE: {
      const { parentId, favorites, deferListMirror } = action.payload;
      // deferListMirror: an optimistic add keeps the events-list split
      // stable until its fan-out settles; SYNC_FAVORITES_FROM_STORAGE
      // catches the mirror up then.
      if (!parentId || deferListMirror) return state;

      if (favorites.length > 0) {
        return { ...state, favoritesByEvent: { ...state.favoritesByEvent, [parentId]: favorites } };
      }

      const { [parentId]: _removed, ...rest } = state.favoritesByEvent;
      return { ...state, favoritesByEvent: rest };
    }

    case EventActionTypes.SYNC_FAVORITES_FROM_STORAGE:
      return { ...state, favoritesByEvent: action.payload };

    default:
      return state;
  }
}
